import { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cn from 'classnames';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowSquareUp, faExternalLink, faPlus, faTimes, faChevronDown } from '@fortawesome/pro-light-svg-icons';

import Loader from '../General/Loader';
import './AllBrandsEditResult.scss';
import { getSmartImage } from '../../helpers/helpers';

const numberFormatter = new Intl.NumberFormat();

const getDomainsFromBrand = brand => {
  if (brand.domains?.length) return brand.domains;
  const result = [];
  if (brand.domain) result.push({ domain: brand.domain, isPrimary: true, isPreownedRetailer: false });
  if (brand.altDomains?.length) {
    result.push(...brand.altDomains.map(d => ({ domain: d, isPrimary: false, isPreownedRetailer: false })));
  }
  return result;
};

const AllBrandsEditResult = props => {
  const {
    brand,
    industries,
    departments,
    conflicts,
    hasEdits,
    isSelected,
    isSaving,
    isNewBrand,
    onSelect,
    onEdit,
    onSave,
    onCancel,
    onMerge,
    onUnarchive
  } = props;

  const domainInputRefs = useRef({});
  const [domainsExpanded, setDomainsExpanded] = useState(false);

  const domainEntries = getDomainsFromBrand(brand);
  const domainsSummary = domainEntries.map(d => d.domain).filter(Boolean).join(', ') || 'No domains';

  const editDomains = useCallback(
    newDomains => {
      onEdit(brand.id, { domains: newDomains });
    },
    [brand.id, onEdit]
  );

  const handleDomainChange = (index, value) => {
    if (value.includes(',')) {
      // Split on commas — handles both single comma (typing) and multi-comma (paste)
      const parts = value.split(',').map(s => s.trim()).filter(Boolean);
      // Trailing comma (e.g. typing "," after a domain) → add an empty row
      const trailingComma = value.trimEnd().endsWith(',');
      if (!domainEntries.length) {
        const entries = parts.map((d, i) => ({ domain: d, isPrimary: i === 0, isPreownedRetailer: false }));
        if (trailingComma) entries.push({ domain: '', isPrimary: false, isPreownedRetailer: false });
        editDomains(entries);
        setTimeout(() => domainInputRefs.current[entries.length - 1]?.focus(), 0);
        return;
      }
      const updated = [...domainEntries];
      updated[index] = { ...updated[index], domain: parts[0] };
      const newEntries = parts.slice(1).map(d => ({ domain: d, isPrimary: false, isPreownedRetailer: false }));
      if (trailingComma) newEntries.push({ domain: '', isPrimary: false, isPreownedRetailer: false });
      updated.splice(index + 1, 0, ...newEntries);
      editDomains(updated);
      setTimeout(() => domainInputRefs.current[index + newEntries.length]?.focus(), 0);
      return;
    }
    if (domainEntries.length) {
      const updated = [...domainEntries];
      updated[index] = { ...updated[index], domain: value };
      editDomains(updated);
    } else {
      editDomains([{ domain: value, isPrimary: true, isPreownedRetailer: false }]);
    }
  };

  const handleDomainKeyDown = (index, e) => {
    // Backspace on empty field removes the row and focuses previous
    if (e.key === 'Backspace' && !domainEntries[index].domain && domainEntries.length > 1) {
      e.preventDefault();
      const updated = domainEntries.filter((_, i) => i !== index);
      // If we removed the primary, make the first one primary
      if (domainEntries[index].isPrimary && updated.length) {
        updated[0] = { ...updated[0], isPrimary: true };
      }
      editDomains(updated);
      setTimeout(() => domainInputRefs.current[Math.max(0, index - 1)]?.focus(), 0);
    }
  };

  const togglePreowned = index => {
    const updated = [...domainEntries];
    updated[index] = { ...updated[index], isPreownedRetailer: !updated[index].isPreownedRetailer };
    editDomains(updated);
  };

  const removeDomain = index => {
    const updated = domainEntries.filter((_, i) => i !== index);
    if (domainEntries[index].isPrimary && updated.length) {
      updated[0] = { ...updated[0], isPrimary: true };
    }
    editDomains(updated);
  };

  const addDomain = () => {
    const updated = [...domainEntries, { domain: '', isPrimary: !domainEntries.length, isPreownedRetailer: false }];
    editDomains(updated);
    setTimeout(() => domainInputRefs.current[updated.length - 1]?.focus(), 0);
  };

  const addDepartment = departmentId => {
    onEdit(brand.id, {
      departments: _.uniqBy(
        [...(brand.departments ?? []), { Department_id: departmentId, name: _.find(departments, { id: departmentId }).name }],
        'Department_id'
      )
    });
  };
  const removeDepartment = departmentId => {
    onEdit(brand.id, { departments: (brand.departments ?? []).filter(({ Department_id }) => Department_id !== departmentId) });
  };

  const nameConflicts = conflicts.filter(conflict => conflict.field === 'name');
  const domainConflicts = conflicts.filter(conflict => conflict.field === 'domain');

  const onSubmit = e => {
    e.preventDefault();
    onSave(brand.id);
  };

  const logo = getSmartImage(brand.logo);

  return (
    <form
      className={cn(
        'all-brands-edit-results-row',
        isNewBrand && 'all-brands-edit-results-row--new-brand',
        brand.isArchived && 'all-brands-edit-results-row--archived'
      )}
      onSubmit={e => onSubmit(e)}
    >
      <input type='checkbox' checked={isSelected} onChange={() => onSelect(brand.id)} />
      <div className='all-brands-results-info'>
        <div
          className={cn('all-brands-results-logo', !logo && 'all-brands-results-logo--missing')}
          onClick={() => {
            if (logo) window.open(logo, '_blank');
          }}
        >
          {logo && <img src={logo} />}
        </div>
        <button
          type='button'
          className='all-brands-results-logo-upload'
          onClick={() => {
            const newLogo = window.prompt('Enter a new logo URL');
            onEdit(brand.id, { logo: newLogo });
          }}
        >
          <FontAwesomeIcon icon={faArrowSquareUp} />
        </button>
        <div className='all-brands-results-name-row'>
          <input
            id={`${brand.id}-name`}
            className={cn('all-brands-results-name', (!brand.name || nameConflicts.length > 0) && 'all-brands-results-name--error')}
            type='text'
            value={brand.name ?? ''}
            placeholder='Brand name'
            onChange={e => onEdit(brand.id, { name: e.target.value })}
            autoFocus={isNewBrand}
          />
          {brand.isArchived && <span className='all-brands-archived-badge'>Archived</span>}
        </div>
        {nameConflicts.length > 0 && (
          <p className='all-brands-results-name-error'>
            There is another brand with this name{' '}
            <button onClick={() => onMerge([brand.id, ..._.map(nameConflicts, 'id')])}>merge with conflicting brand</button>
          </p>
        )}
        <div className='all-brands-results-domains-container'>
          {domainsExpanded ? (
            <>
              {domainEntries.map((entry, index) => (
                <div key={index} className='all-brands-domain-row'>
                  <input
                    ref={el => (domainInputRefs.current[index] = el)}
                    className={cn('all-brands-results-domains', domainConflicts.length > 0 && 'all-brands-results-domains--error')}
                    type='text'
                    value={entry.domain}
                    placeholder={index === 0 ? 'domain.com' : 'alt domain'}
                    onChange={e => handleDomainChange(index, e.target.value)}
                    onKeyDown={e => handleDomainKeyDown(index, e)}
                  />
                  {entry.domain && (
                    <a href={`https://${entry.domain}`} target='_blank' rel='noopener noreferrer' className='all-brands-domain-link'>
                      <FontAwesomeIcon icon={faExternalLink} />
                    </a>
                  )}
                  <label className='all-brands-domain-preowned'>
                    <input type='checkbox' checked={entry.isPreownedRetailer} onChange={() => togglePreowned(index)} />
                    <span>Pre-owned</span>
                  </label>
                  {entry.isPrimary && <span className='all-brands-domain-primary-badge'>primary</span>}
                  {domainEntries.length > 1 && (
                    <button type='button' className='all-brands-domain-remove' onClick={() => removeDomain(index)}>
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>
              ))}
              {domainEntries.length === 0 && (
                <div className='all-brands-domain-row'>
                  <input
                    ref={el => (domainInputRefs.current[0] = el)}
                    className={cn('all-brands-results-domains', 'all-brands-results-domains--error')}
                    type='text'
                    value=''
                    placeholder='domain.com'
                    onChange={e => handleDomainChange(0, e.target.value)}
                  />
                </div>
              )}
              <button type='button' className='all-brands-domain-add' onClick={addDomain}>
                <FontAwesomeIcon icon={faPlus} /> add domain
              </button>
            </>
          ) : (
            <button type='button' className='all-brands-domains-summary' onClick={() => setDomainsExpanded(true)}>
              <span className={cn(!domainEntries.length && 'all-brands-domains-summary--empty')}>{domainsSummary}</span>
              <FontAwesomeIcon icon={faChevronDown} />
            </button>
          )}
        </div>
        {domainConflicts.length > 0 && (
          <p className='all-brands-results-domain-error'>
            {_.map(domainConflicts, 'value').join(', ')} conflicts with another brand{' '}
            <button onClick={() => onMerge([brand.id, ..._.map(domainConflicts, 'id')])}>merge with conflicting brands</button>
          </p>
        )}
        {!isNewBrand && (
          <label
            className='all-brands-results-hide-cross-brand'
            title={`Hides the other-brand recommendation rows on this brand's product pages on web, iOS and the Snapshop panel. Cached pages catch up within an hour.`}
          >
            <input
              type='checkbox'
              checked={Boolean(brand.hideCrossBrandRecommendations)}
              onChange={e => onEdit(brand.id, { hideCrossBrandRecommendations: e.target.checked })}
            />
            <span>Hide other-brand recommendations</span>
          </label>
        )}
      </div>
      <Select
        className='all-brands-results-industries'
        value={{ value: brand.industryId ?? 0, label: brand.industryName ?? 'No industry' }}
        options={[{ value: 0, label: 'No industry' }, ...industries.map(industry => ({ value: industry.id, label: industry.name }))]}
        onChange={e => {
          onEdit(brand.id, { industryId: e.value, industryName: e.label });
        }}
        unstyled
      />
      <div className='all-brands-results-tags'>
        <div className='all-brands-results-tags-list'>
          {(brand.departments ?? []).map(({ Department_id, name }) => (
            <button key={Department_id} type='button' onClick={() => removeDepartment(Department_id)}>
              {name} Ｘ
            </button>
          ))}
        </div>
        <Select
          className='all-brands-results-department-select'
          value={undefined}
          options={departments.map(department => ({ value: department.id, label: department.name }))}
          styles={{
            control: base => ({
              ...base,
              minHeight: '26px',
              height: '26px'
            }),
            valueContainer: base => ({
              ...base,
              height: '26px',
              padding: '0 6px'
            }),
            input: base => ({
              ...base,
              margin: '0px',
              padding: '0px'
            }),
            indicatorsContainer: base => ({
              ...base,
              height: '26px'
            })
          }}
          placeholder='Add department'
          onChange={e => {
            addDepartment(e.value);
          }}
          unstyled
        />
      </div>
      <div className='all-brands-promoters'>{numberFormatter.format(brand.numPromoting)}</div>
      {hasEdits && (
        <div className='all-brands-results-actions'>
          <button type='submit' className='all-brands-results-actions-save' disabled={isSaving}>
            {isSaving ? <Loader /> : isNewBrand ? 'Add Brand' : 'Save'}
          </button>
          <button type='button' className='all-brands-results-actions-cancel' disabled={isSaving} onClick={() => onCancel(brand.id)}>
            {isSaving ? <Loader /> : 'Cancel'}
          </button>
        </div>
      )}
      {brand.isArchived && !isNewBrand && (
        <div className='all-brands-results-actions'>
          <button type='button' className='all-brands-results-actions-unarchive' onClick={() => onUnarchive(brand.id)}>
            Unarchive
          </button>
        </div>
      )}
    </form>
  );
};

AllBrandsEditResult.propTypes = {
  brand: PropTypes.object.isRequired,
  industries: PropTypes.array.isRequired,
  departments: PropTypes.array.isRequired,
  conflicts: PropTypes.array.isRequired,
  hasEdits: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  isNewBrand: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onMerge: PropTypes.func.isRequired,
  onUnarchive: PropTypes.func
};

export default AllBrandsEditResult;
